import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  DateField,
  Field,
  Page,
  PageHeader,
  SearchSelect,
  cn,
} from '@evogard/ui';
import {
  businessDate,
  usageLineOf,
  usageWindowLabel,
  type AssistanceServiceType,
  type UsageRuleKind,
  type UsageWindowUnit,
} from '@evogard/domain';
import type { UsageRowValue, UsageRulesWrite, VehicleUsage } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { ASSISTANCE_KEY } from './assistance.js';

export const USAGE_KEY = [...ASSISTANCE_KEY, 'cota'] as const;

/** A cota de uma placa, relida a cada escolha de veículo. */
export function useVehicleUsage(vehicleId: number | null) {
  return useQuery({
    queryKey: [...USAGE_KEY, vehicleId],
    enabled: vehicleId !== null,
    queryFn: async () =>
      ok(
        await api.assistanceUsage.vehicleUsage({
          params: { vehicleId: vehicleId! },
        }),
        200,
        'Não foi possível ler a cota desta placa.',
      ),
  });
}

/**
 * A linha do serviço escolhido. No reboque, ela só existe depois do motivo: a
 * cota é do motivo, e adivinhar pane travaria um sinistro.
 */
export function usageRowFor(
  usage: VehicleUsage | undefined,
  serviceType: AssistanceServiceType,
  serviceVariant: string | null,
): UsageRowValue | null {
  if (!usage) return null;
  if (serviceType === 'reboque' && serviceVariant === null) return null;
  const line = usageLineOf(serviceType, serviceVariant);
  return (
    usage.rows.find(
      (r) =>
        r.serviceType === line.serviceType && r.serviceVariant === line.serviceVariant,
    ) ?? null
  );
}

function quotaLabel(row: UsageRowValue, shared: VehicleUsage['window']): string {
  if (row.kind === 'nao_definido') return 'Não definida';
  if (row.kind === 'sem_limite' || row.maxUses === null) return 'Sem limite';
  const sameWindow =
    row.window.amount === shared.amount && row.window.unit === shared.unit;
  return sameWindow
    ? String(row.maxUses)
    : `${row.maxUses} em ${usageWindowLabel(row.window)}`;
}

function UsageTable({
  rows,
  shared,
  current,
}: {
  rows: UsageRowValue[];
  shared: VehicleUsage['window'];
  current: UsageRowValue | null;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-borda-forte text-left text-xs font-semibold">
          <th scope="col" className="px-2 py-1.5">
            Serviço
          </th>
          <th scope="col" className="px-2 py-1.5 text-center">
            Cota
          </th>
          <th scope="col" className="px-2 py-1.5 text-center">
            Uso
          </th>
          <th scope="col" className="px-2 py-1.5 text-center">
            Pode acionar?
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const chosen =
            current?.serviceType === r.serviceType &&
            current.serviceVariant === r.serviceVariant;
          return (
            <tr
              key={r.label}
              className={cn(
                'border-b border-borda last:border-b-0',
                !r.canDispatch && 'bg-erro-suave',
                r.canDispatch && chosen && 'bg-primaria-suave',
                chosen && 'font-bold',
              )}
            >
              <td className="px-2 py-1.5">{r.label}</td>
              <td className="px-2 py-1.5 text-center tabular-nums">
                {quotaLabel(r, shared)}
              </td>
              <td className="px-2 py-1.5 text-center tabular-nums">{r.used}</td>
              <td
                className={cn(
                  'px-2 py-1.5 text-center font-bold',
                  r.canDispatch ? 'text-sucesso' : 'text-erro',
                )}
              >
                {r.canDispatch ? 'Sim' : 'Não'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * A cota da placa, acima da escolha do serviço (01/11/2026). É uma tabela de
 * números, e não frases nos ladrilhos, porque a central tem muita gente nova:
 * "1 e 1, pode acionar? Não" não pede interpretação. Por isso também não diz
 * "esgotado" nem "volta em".
 */
export function UsagePanel({
  usage,
  loading,
  error,
  current,
}: {
  usage: VehicleUsage | undefined;
  loading: boolean;
  error: string | null;
  current: UsageRowValue | null;
}) {
  if (error) return <Alert tone="erro">{error}</Alert>;
  if (loading || !usage) {
    return <p className="text-sm text-texto-suave">Lendo a cota desta placa…</p>;
  }
  if (!usage.anyRule) {
    return (
      <Alert tone="informacao">
        Esta base ainda não definiu as cotas do regulamento, e nada é conferido.
      </Alert>
    );
  }
  const half = Math.ceil(usage.rows.length / 2);
  return (
    <section
      aria-label="Cota desta placa"
      className="flex flex-col gap-2 rounded-xl border border-borda-forte p-3"
    >
      <div>
        <h3 className="text-base font-bold text-texto">
          Cota desta placa nos últimos {usageWindowLabel(usage.window)}
        </h3>
        <p className="text-sm text-texto-suave">
          Cota é quanto pode usar. Uso é quanto já usou.
        </p>
      </div>
      <div className="grid gap-x-5 gap-y-2 lg:grid-cols-2">
        <UsageTable
          rows={usage.rows.slice(0, half)}
          shared={usage.window}
          current={current}
        />
        <UsageTable
          rows={usage.rows.slice(half)}
          shared={usage.window}
          current={current}
        />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------- *
 * A configuração
 * ------------------------------------------------------------------------- */

// @valor
const KIND_LABEL: Readonly<Record<UsageRuleKind, string>> = {
  limitado: 'Com limite',
  sem_limite: 'Sem limite',
  nao_definido: 'Não definida',
};

interface LineForm {
  serviceType: AssistanceServiceType;
  serviceVariant: UsageRulesWrite['lines'][number]['serviceVariant'];
  label: string;
  kind: UsageRuleKind;
  maxUses: string;
  amount: string;
  unit: UsageWindowUnit;
  regulationText: string;
}

const positive = (text: string) => {
  const n = Number(text);
  return Number.isInteger(n) && n >= 1 ? n : null;
};

/**
 * As cotas do regulamento, por base. Tudo aqui muda sem desenvolvimento: o
 * regulamento muda, e a regra anterior fica guardada para os acionamentos dela.
 */
export function UsageRulesScreen() {
  const client = useQueryClient();
  const today = businessDate(new Date());
  const query = useQuery({
    queryKey: [...USAGE_KEY, 'regras'],
    queryFn: async () =>
      ok(await api.assistanceUsage.rules(), 200, 'Não foi possível ler as cotas.'),
  });
  const [edited, setEdited] = useState<LineForm[] | null>(null);
  const [grace, setGrace] = useState<string | null>(null);
  const [validFrom, setValidFrom] = useState(today);
  const lines: LineForm[] | null =
    edited ??
    query.data?.lines.map((l) => ({
      serviceType: l.serviceType,
      serviceVariant: l.serviceVariant,
      label: l.label,
      kind: l.kind,
      maxUses: l.maxUses?.toString() ?? '',
      amount: l.window?.amount.toString() ?? '30',
      unit: l.window?.unit ?? 'dias',
      regulationText: l.regulationText ?? '',
    })) ??
    null;
  const graceText = grace ?? query.data?.cancelGraceMin?.toString() ?? '';
  const graceNumber = graceText.trim() === '' ? null : Number(graceText);
  const graceValid =
    graceNumber === null ||
    (Number.isInteger(graceNumber) && graceNumber >= 0 && graceNumber <= 1440);
  const lineValid = (l: LineForm) =>
    l.kind !== 'limitado' ||
    (positive(l.maxUses) !== null && positive(l.amount) !== null);
  const valid =
    lines !== null && lines.every(lineValid) && graceValid && validFrom >= today;

  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceUsage.saveRules({
          body: {
            validFrom,
            cancelGraceMin: graceNumber,
            lines: lines!.map((l) => ({
              serviceType: l.serviceType,
              serviceVariant: l.serviceVariant,
              kind: l.kind,
              maxUses: l.kind === 'limitado' ? positive(l.maxUses) : null,
              window:
                l.kind === 'limitado'
                  ? { amount: positive(l.amount)!, unit: l.unit }
                  : null,
              regulationText: l.regulationText.trim() || null,
            })),
          },
        }),
        200,
        'Não foi possível gravar as cotas.',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: USAGE_KEY });
      setEdited(null);
      setGrace(null);
    },
  });
  const change = (index: number, patch: Partial<LineForm>) =>
    setEdited(lines!.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  return (
    <Page measure="cheia">
      <PageHeader title="Cotas do regulamento">
        Quantas vezes cada serviço pode ser acionado nesta base. A regra anterior fica
        guardada, e cada acionamento continua apontando para a que valeu.
      </PageHeader>
      {(query.error ?? save.error) && (
        <Alert tone="erro">{(query.error ?? save.error)!.message}</Alert>
      )}
      {lines && (
        <Card>
          <div className="flex flex-col gap-3">
            {lines.map((l, i) => (
              <fieldset
                key={l.label}
                className="grid items-start gap-2 border-b border-borda pb-3 md:grid-cols-[11rem_9.5rem_5rem_5rem_6.5rem_minmax(0,1fr)]"
              >
                <legend className="sr-only">{l.label}</legend>
                <p className="pt-7 text-sm font-semibold">{l.label}</p>
                <SearchSelect
                  label="Limite"
                  options={(Object.keys(KIND_LABEL) as UsageRuleKind[]).map((k) => ({
                    value: k,
                    label: KIND_LABEL[k],
                  }))}
                  value={l.kind}
                  onChange={(v) =>
                    change(i, { kind: (v || 'nao_definido') as UsageRuleKind })
                  }
                />
                <Field
                  label="Quantidade"
                  inputMode="numeric"
                  disabled={l.kind !== 'limitado'}
                  value={l.kind === 'limitado' ? l.maxUses : ''}
                  onChange={(e) => change(i, { maxUses: e.target.value })}
                />
                <Field
                  label="A cada"
                  inputMode="numeric"
                  disabled={l.kind !== 'limitado'}
                  value={l.kind === 'limitado' ? l.amount : ''}
                  onChange={(e) => change(i, { amount: e.target.value })}
                />
                <SearchSelect
                  label="Unidade"
                  disabled={l.kind !== 'limitado'}
                  options={[
                    { value: 'dias', label: 'dias' },
                    { value: 'meses', label: 'meses' },
                  ]}
                  value={l.unit}
                  onChange={(v) => change(i, { unit: (v || 'dias') as UsageWindowUnit })}
                />
                <Field
                  label="Texto do regulamento"
                  maxLength={500}
                  value={l.regulationText}
                  onChange={(e) => change(i, { regulationText: e.target.value })}
                />
              </fieldset>
            ))}
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <Field
              label="Desistência que não gasta a cota"
              suffix="min"
              inputMode="numeric"
              value={graceText}
              onChange={(e) => setGrace(e.target.value)}
              error={graceValid ? null : 'Informe de 0 a 1440 minutos.'}
              help="Cancelado até este tempo depois de acionar não gasta. Depois disso, gasta. Vazio: cancelado nenhum gasta."
            />
            <DateField
              label="Passa a valer em"
              required
              value={validFrom}
              onChange={setValidFrom}
              error={validFrom < today ? 'A regra nova vale de hoje em diante.' : null}
              help="Vale para as linhas que mudaram. A desistência muda na hora."
            />
          </div>
          {/* Ao lado do botão: a tela é longa, e o topo fica fora da vista de quem salvou. */}
          {save.isSuccess && !edited && grace === null && (
            <div className="mt-4">
              <Alert tone="sucesso">Cotas gravadas.</Alert>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <Button
              disabled={!valid || save.isPending}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Salvar cotas
            </Button>
          </div>
        </Card>
      )}
    </Page>
  );
}
